// Applying a saved layout to a new send.
//
// CONSUMER: shared (Workers route + node-pool tests).
//
// A layout remembers ORDINALS — "the first signer signs here, the second
// initials there" — because it is reused across households and cannot
// know today's people. This is where that becomes real fields for real
// contacts, and where the mismatches get reported instead of hidden.
//
// Pure and dependency-free so the mapping — the part that decides whose
// signature goes where on a client's contract — is tested directly.

import { PREPARER_SIGNER_KEY } from "./field-types";
import type { FieldAnchor } from "./layout-anchors";
import type { SuggestedField } from "./field-suggestions";
import type { EsignFieldType } from "./field-types";
import { parseTextStyle, type EsignTextStyle } from "./text-style";

export interface LayoutFieldInput {
  signerIndex: number;
  signerRole: string | null;
  /** THE SEAT THIS FIELD BELONGS TO (2026-09-15), when the map was
   *  taught by a screen that knew its seats — `buyer_2`, `co_borrower`,
   *  `grantor_1`, from shared/esign/signing-roles.ts.
   *
   *  The ordinal above is where a layout has always stored an owner,
   *  and it is right exactly while today's signer list is the same
   *  SHAPE as the one that taught it. It stops being right the moment
   *  somebody unticks a signer, lists the co-borrower first, or drops a
   *  co-op agent between the two buyers — and then Buyer 2's signature
   *  line lands on whoever happens to be second, which looks finished
   *  and goes out.
   *
   *  Null on every map taught before seats existed and on any save that
   *  did not know them; null means "place me by position", which is the
   *  behaviour every existing map relies on. */
  roleKey?: string | null;
  /** HOW THIS BLANK GETS FILLED (2026-09-15) — see fill-rules.ts. A
   *  short string like `record:service_record.property_address`,
   *  `default:realty.buyer_broker_compensation · 3.00%`, `sign`, or
   *  `gap:HOA transfer fee`.
   *
   *  A map has always said where a blank sits and whose it is; this is
   *  the third question, the one that decides whether the sender types
   *  it or the OS already knows it. Null on every map taught before
   *  today and on any field placed by hand, which means "nobody has
   *  said" rather than "nothing fills it". */
  fillRule?: string | null;
  fieldType: EsignFieldType;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  isRequired: boolean;
  value: string | null;
  /** What this field is BOUND to, when it merges record data.
   *
   *  A layout stores the binding and NOT the value it resolved to. That
   *  is the difference between a repeat document that prints this
   *  month's price and one that prints the price from whenever the
   *  layout was taught — and the second would look correct while being
   *  wrong. */
  mergeToken?: string | null;
  /** Dropdown only: the choices this form offered. */
  options?: readonly string[] | null;
  /** Radio only: which buttons rule each other out. */
  groupName?: string | null;
  /** A text box spanning several printed lines, the points between its
   *  rules, and how far in its first line starts — see
   *  EsignPlacedField.multiline (2026-09-29). */
  multiline?: boolean | null;
  linePitch?: number | null;
  firstLineIndent?: number | null;
  /** How a locked text box's type looks (2026-10-26); null/absent is plain. */
  textStyle?: EsignTextStyle | null;
  /** The caption this field was placed against, and how far from it.
   *
   *  Coordinates alone cannot survive a new revision of the document —
   *  a paragraph inserted on page 2 slides everything below it, and the
   *  fingerprint that FINDS this map stops matching at all. The caption
   *  can, which is what lets shared/esign/layout-carryover.ts move the
   *  map onto next year's form instead of making someone redraw it.
   *
   *  Null when nothing usable sat near the field, and on every map saved
   *  before anchors existed. Both mean the same thing downstream: this
   *  field cannot be carried over, and comes back unmapped rather than
   *  guessed. */
  anchor?: FieldAnchor | null;
  /** A term from the offer this send is drafted from, for a box the
   *  form's own map cannot fill (the SCA percentage, an HOA "paid by"
   *  box). Never stored on a map: attached by the send route for this
   *  send only, and offered on the screen as "your offer" (Nate,
   *  2026-11-03; shared/realty/offer-term-boxes.ts). */
  offerTerm?: OfferTermOffer | null;
}

/** What an offer term offers for one box: the value to print, the term's
 *  name, and the value it replaces (the map's own default, if any). */
export interface OfferTermOffer {
  value: string;
  label: string;
}

export interface ApplyLayoutResult {
  /** Ready to render as proposals, in the same shape every other rung
   *  of the ladder produces — so the prep screen treats a remembered
   *  field and a suggested one identically. */
  fields: (SuggestedField & {
    /** WHO THIS BOX BELONGS TO, ALREADY WORKED OUT (Baker, 2026-09-15).
     *
     *  The position below is how a layout STORES an owner; this is the
     *  owner. They were the same thing until a locked box came along,
     *  which belongs to nobody and is therefore stored at position -1 —
     *  and a caller that turned a position back into an owner by
     *  counting into its signer list found nothing at -1. Baker's nine
     *  locked boxes vanished from the mapping screen and arrived on the
     *  send screen as the selected signer's editable text, which is the
     *  same bug wearing two faces.
     *
     *  So the answer travels with the field and nobody re-derives it.
     *  `signerIndex` is kept because saving the map back still needs the
     *  ordinal, but it is no longer the thing you ask "whose is this?" */
    signerKey: string;
    signerIndex: number;
    /** The seat this field was remembered under, carried through so the
     *  next save teaches the same seat rather than re-deriving it from
     *  whoever happens to hold the position today. */
    roleKey: string | null;
    /** Carried through so the prep screen can say where a proposed
     *  value comes from, and so the next save teaches the same rule. */
    fillRule: string | null;
    value: string | null;
    isRequired: boolean;
    mergeToken: string | null;
    options: readonly string[] | null;
    groupName: string | null;
  })[];
  /** The sender's own signature, initials and date-signed lines, lifted
   *  out of `fields` because nobody signs them — see `stampRoleKeys`.
   *  Geometry only: the caller owns both the signature image and the
   *  clock, which is why neither appears here. */
  stamps: {
    roleKey: string;
    fieldType: EsignFieldType;
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }[];
  /** How many the layout held but could not place. */
  unplaced: number;
  /** Always empty since 2026-11-04: a seat nobody fills is now reported
   *  in `emptySeatRoles`, not as an unplaced shortfall. Kept so older
   *  readers of the response keep working. */
  unplacedRoles: string[];
  /** Lines the layout holds for a seat NOBODY is cast into on this send
   *  (the seller's seats on a buyer's offer; Buyer 2 when there is no
   *  second buyer). Left off, not sent, and deliberately NOT counted in
   *  `unplaced` or the note: they do not belong to "another signer", they
   *  belong to a seat this send does not have (Grant, 2026-11-04). */
  emptySeatFields: number;
  /** The seats those lines belong to, for a screen that wants to say so. */
  emptySeatRoles: string[];
  /** Plain English for the panel, or null when everything mapped.
   *  Written for an advisor, not a log: it says what happened and what
   *  to do about it. */
  note: string | null;
}

/**
 * Map a saved layout onto the signers selected for THIS send.
 *
 * The rule is positional: layout signer 0 → today's first signer, 1 →
 * second, and so on. Fields whose ordinal has no signer today are NOT
 * placed and ARE counted.
 *
 * Why not fall back to the first signer for those? Because a contract
 * with the co-borrower's signature line silently reassigned to the
 * borrower is worse than one with a missing line: the missing line is
 * visible on screen and gets placed by hand, while the wrong one looks
 * finished and goes out. This is the same rule the AI pass follows for
 * an ambiguous role hint.
 */
/** The ordinal a preparer's own box is remembered under.
 *
 *  A saved layout addresses fields by the signer's POSITION on the send
 *  that taught it. A preparer's box has no position in that list — it
 *  belongs to nobody — so it is stored below the first one. Chosen over
 *  a new column because the alternative is a table rebuild for a value
 *  that is genuinely just "not one of the signers", and -1 cannot
 *  collide with a real ordinal. */
export const PREPARER_LAYOUT_INDEX = -1;

const EMPTY_ROLE_SET: ReadonlySet<string> = new Set();

/** The three types somebody signs rather than types. These are the ones
 *  a stamp replaces; a text box on the same seat is still a text box. */
function isCeremonyType(t: EsignFieldType): boolean {
  return t === "signature" || t === "initials" || t === "date_signed";
}

/** Who holds each seat on the send a layout is being applied to.
 *
 *  `signerKeyByRole` is the casting: seat key → the signer key that
 *  seat's fields belong to. `labelByRole` is only for the message a
 *  sender reads, so an empty seat can be named.
 *
 *  Absent entirely (the caller has no casting — an older client, a
 *  screen that never cast) means every field is placed by position,
 *  exactly as before seats existed. */
export interface LayoutSeatMap {
  signerKeyByRole: ReadonlyMap<string, string>;
  labelByRole?: ReadonlyMap<string, string>;
  /** Seats the SENDER THEMSELVES fills, by stamping rather than signing
   *  (Grant, 2026-08-22: we never email ourselves an envelope). Their
   *  signature, initials and date-signed lines come back in `stamps`
   *  instead of `fields`, because there is no signer to attach them to
   *  and no ceremony in which anyone would fill them.
   *
   *  ONLY the seat holding the person doing the sending belongs here.
   *  The broker's initials and a second agent's signature are also
   *  stamp-kind seats, and they are somebody ELSE's hand — stamping the
   *  sender's signature on those is forgery, not convenience. A caller
   *  that cannot prove the seat is the sender's own leaves it out, and
   *  its fields come back unplaced exactly as before. */
  stampRoleKeys?: ReadonlySet<string>;
}

export function applyLayout(
  layoutFields: LayoutFieldInput[],
  /** This send's signer keys IN ORDER — position 0 is whoever signs
   *  first. Ordinary contact ids for our own people, and a prep-screen
   *  key for a signer who isn't one of our contacts; the mapping is
   *  positional either way, so a layout taught with two clients applies
   *  unchanged to a client and a co-op agent. */
  signerKeys: string[],
  layoutName: string,
  /** The seats this send cast, when it cast any. */
  seats?: LayoutSeatMap,
): ApplyLayoutResult {
  const fields: ApplyLayoutResult["fields"] = [];
  const stamps: ApplyLayoutResult["stamps"] = [];
  let unplaced = 0;
  let emptySeatFields = 0;
  const emptySeatRoles = new Set<string>();
  const missingIndexes = new Set<number>();
  const hasCasting = Boolean(seats && seats.signerKeyByRole.size > 0);
  const stampRoles = seats?.stampRoleKeys ?? EMPTY_ROLE_SET;

  for (const f of layoutFields) {
    // The preparer's own boxes come back on every send of this form,
    // which is the point of remembering them: Joey places the locked
    // boxes on the template once. Whether the VALUE comes back too
    // depends on where it was typed — see layoutFieldsFromSend.
    //
    // THE SEAT WINS OVER THE POSITION, and only when BOTH sides know
    // one (2026-09-15). A field remembered as Buyer 2's goes to whoever
    // is cast as Buyer 2 today, wherever they sit in the list. If this
    // send cast no seats at all, or the map was taught before seats
    // existed, nothing here changes and the ordinal decides — which is
    // every map saved before today.
    //
    // A KEYED FIELD WHOSE SEAT IS EMPTY IS NOT PLACED. Falling back to
    // its old position there would be the exact failure seats exist to
    // stop: the co-borrower's line landing on the borrower because the
    // co-borrower was unticked. A missing line is visible on screen and
    // gets placed by hand; a wrong one looks finished and goes out.
    // THE SENDER'S OWN LINES, BEFORE ANY OF THE SEAT MATH.
    //
    // Their seat never had a signer key and never will — that is what
    // "stamped, not emailed" means — so the code below would count
    // every one of these as unplaced and then tell the sender their
    // seat is one "nobody is in on this send". They are in it. They are
    // the one sending. Eighty of these sit in the Realty atlas alone
    // (the agent's and the broker's signature, initials and date on
    // every form that asks for them), and until now each was a line the
    // advisor had to notice was missing and place by hand.
    //
    // Only the CEREMONY types are lifted. Everything else a stamp seat
    // owns — the agent's printed name, MLS code and licence — is a
    // preparer box already and is placed by the ordinary path below.
    if (f.roleKey && stampRoles.has(f.roleKey) && isCeremonyType(f.fieldType)) {
      stamps.push({
        roleKey: f.roleKey,
        fieldType: f.fieldType,
        pageNumber: f.pageNumber,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
      });
      continue;
    }
    const seatKey = hasCasting && f.roleKey ? seats?.signerKeyByRole.get(f.roleKey) : undefined;
    const signerKey =
      f.signerIndex === PREPARER_LAYOUT_INDEX
        ? PREPARER_SIGNER_KEY
        : hasCasting && f.roleKey
          ? seatKey
          : signerKeys[f.signerIndex];
    if (!signerKey) {
      if (hasCasting && f.roleKey) {
        // A SEAT NOBODY IS IN is not "another signer" (Grant, 2026-11-04).
        // The line is left off and not sent; counting it as unplaced is
        // what made a buyer's offer read "the rest belong to 4 other
        // signers" over the seller's seats. Still never reassigned to
        // someone else: this is the same refusal, said honestly.
        emptySeatFields += 1;
        emptySeatRoles.add(seats?.labelByRole?.get(f.roleKey) ?? f.signerRole ?? f.roleKey);
      } else {
        unplaced += 1;
        missingIndexes.add(f.signerIndex);
      }
      continue;
    }
    fields.push({
      fieldType: f.fieldType,
      pageNumber: f.pageNumber,
      x: f.x,
      y: f.y,
      width: f.width,
      height: f.height,
      source: "saved_layout",
      // The highest confidence any rung carries: this is not a reading
      // of the document, it is what the team actually did to this exact
      // document last time.
      confidence: 0.98,
      reason: f.signerRole
        ? `${f.signerRole} — from "${layoutName}"`
        : `From "${layoutName}"`,
      signerHint: f.signerRole,
      // Resolved above, handed over rather than left to be recomputed.
      signerKey,
      signerIndex: f.signerIndex,
      roleKey: f.roleKey ?? null,
      fillRule: f.fillRule ?? null,
      value: f.value,
      isRequired: f.isRequired,
      mergeToken: f.mergeToken ?? null,
      options: f.options ?? null,
      groupName: f.groupName ?? null,
      // A multi-line box comes back as one, at the pitch it was taught.
      ...multilineOf(f),
      ...styleOf(f),
      ...(f.offerTerm ? { offerTerm: f.offerTerm } : {}),
    });
  }

  let note: string | null = null;
  if (unplaced > 0) {
    const who =
      missingIndexes.size === 1
        ? "another signer this document was set up for"
        : `${missingIndexes.size} other signers this document was set up for`;
    note =
      // Stamps count as placed. They ARE placed — on the page, over the
      // sender's own lines — and a note that left them out would report
      // a shortfall the sender can see is not there. Lines for a seat
      // nobody is in are not in the total either: they are not this
      // send's lines at all.
      `Placed ${fields.length + stamps.length} of ${layoutFields.length - emptySeatFields} fields from "${layoutName}". ` +
      `The rest belong to ${who} — fill that seat above, or place those fields yourself.`;
  }

  return {
    fields,
    stamps,
    unplaced,
    unplacedRoles: [],
    emptySeatFields,
    emptySeatRoles: [...emptySeatRoles],
    note,
  };
}

/**
 * The reverse: turn the fields a send is about to go out with into the
 * layout rows that teach it.
 *
 * The ordinal is the signer's position in the send's OWN signer list,
 * which is the same order the prep screen shows and the same order the
 * next application will map back onto.
 */
export function layoutFieldsFromSend(
  placed: {
    signerKey: string;
    /** The seat a LOCKED box reads its person from — see
     *  EsignPlacedField.roleKey. Kept through the save so the map
     *  remembers it; null on every other field. */
    roleKey?: string | null;
    fieldType: EsignFieldType;
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
    required: boolean;
    value?: string | null;
    mergeToken?: string | null;
    options?: readonly string[] | null;
    groupName?: string | null;
    multiline?: boolean | null;
    linePitch?: number | null;
    firstLineIndent?: number | null;
    textStyle?: EsignTextStyle | null;
    /** A strike-through the sender drew on THIS send (toolbar program PR 4a).
     *  Never remembered: it answers "which clause does not apply to this
     *  deal", and a map that brought it back would strike the next
     *  household's clause for them. */
    strike?: boolean | null;
    /** The caption this field was placed against, computed where the
     *  document's text layer actually is — in the browser, by
     *  anchorForBox — and carried through untouched. Absent (an older
     *  client, a scan with no text) simply means this field cannot be
     *  carried onto a future revision. */
    anchor?: FieldAnchor | null;
  }[],
  signerKeys: string[],
  rolesBySignerKey: Map<string, string | null> = new Map(),
  // Static values the PREVIOUS saved layout for this document already
  // carried. DECISION (2026-08-30 audit, S12): an unbound text value is
  // taught only when it appears here — i.e. it has now shown up on two
  // independent sends, which is what boilerplate looks like. The old
  // behavior stored every typed value verbatim, so the Smiths' "$5,000"
  // earnest money re-appeared pre-placed at 0.98 confidence on the
  // Garcias' copy of the same contract — one household's data on
  // another's legal document, looking exactly like correct prefill.
  // First send of a form (no previous layout): positions and bindings
  // teach; typed values don't.
  stableValues: ReadonlySet<string> = new Set(),
  /**
   * Is this teaching a BLANK FORM rather than recording a real send?
   *
   * Baker, 2026-09-15: "All of those boxes either had 0.0% or $0.00
   * prefilled, so that whoever is sending out the package could edit
   * them. Now they say nothing."
   *
   * They said nothing because a locked box's typed value was never
   * remembered — I reasoned it was that loan's data. On a real contract
   * it is. On the mapping screen it cannot be: there is no household and
   * no loan there, only numbered placeholders, and a figure typed
   * against a blank form is a default for every future send by
   * construction. That is the whole job of that screen.
   *
   * So the two paths answer differently, and the distinction is WHERE
   * the value was typed, not what it looks like. Mapping a blank form:
   * a locked box's value is boilerplate and is kept. A real send to a
   * real family: it is their data, and the S12 rule above still governs.
   */
  teachingFromBlankForm = false,
  /** The seat each signer was cast into on the send that is teaching
   *  this map, so the next application can place by seat rather than by
   *  position. An empty map (no casting) teaches ordinals only — which
   *  is what every save did before 2026-09-15.
   *
   *  LAST on purpose: every existing caller passes its arguments
   *  positionally, and slotting a new one in the middle would have
   *  quietly handed `teachingFromBlankForm`'s `true` to this. */
  roleKeyBySignerKey: ReadonlyMap<string, string | null> = new Map(),
): LayoutFieldInput[] {
  const out: LayoutFieldInput[] = [];
  for (const f of placed) {
    if (f.strike === true) continue;
    const isPreparer = f.signerKey === PREPARER_SIGNER_KEY;
    const signerIndex = isPreparer ? PREPARER_LAYOUT_INDEX : signerKeys.indexOf(f.signerKey);
    // A field for somebody not on the send can't be positioned in the
    // ordinal scheme; it also can't have been sent, so it isn't a thing
    // that happened and isn't worth remembering. The preparer's own box
    // is the one legitimate negative — see PREPARER_LAYOUT_INDEX.
    if (signerIndex < 0 && !isPreparer) continue;
    // A PREPARER'S TYPED VALUE IS KEPT ONLY WHEN THE BLANK FORM TAUGHT
    // IT — see teachingFromBlankForm. On the mapping screen there is no
    // household to leak from, so "$0.00" is a default for every future
    // send. On a real contract the same box holds that family's figures,
    // and remembering those would pre-fill them on the next family's
    // copy at full confidence, which is the S12 failure in its purest
    // form. Same box, opposite answer, decided by where it was typed.
    const staticValue =
      isPreparer && teachingFromBlankForm
        ? (f.value ?? null)
        : isPreparer
          ? null
          : f.mergeToken ||
              f.value === undefined ||
              f.value === null ||
              !stableValues.has(f.value)
            ? null
            : f.value;
    out.push({
      signerIndex,
      signerRole: rolesBySignerKey.get(f.signerKey) ?? null,
      // A signer's box takes its seat from who was cast into it. The
      // preparer's box has no signer to look up, so it keeps the seat
      // it already carries — which is null on the ordinary locked box
      // holding boilerplate, and the person's chair on the one Baker
      // found: a locked box printing the BUYER'S NAME, which must be
      // uneditable by the signer and still come from that buyer.
      //
      // This line used to read `isPreparer ? null`, on the reasoning
      // that a box belonging to nobody carries no seat. True of the
      // owner, false of the source: such a box names no person but it
      // does name a chair, and dropping the chair here is what made
      // the map forget, so the next send printed nothing again.
      roleKey: isPreparer ? (f.roleKey ?? null) : (roleKeyBySignerKey.get(f.signerKey) ?? null),
      fieldType: f.fieldType,
      pageNumber: f.pageNumber,
      x: f.x,
      y: f.y,
      width: f.width,
      height: f.height,
      isRequired: f.required,
      // A BOUND field teaches its binding, not the value that binding
      // resolved to on this send — see LayoutFieldInput.mergeToken.
      // An UNBOUND value teaches only once it has repeated (above).
      value: staticValue,
      mergeToken: f.mergeToken ?? null,
      // Unlike `value`, the option list is NOT record data that could go
      // stale — it is the form's own wording, so remembering it verbatim
      // is exactly right.
      options: f.options ?? null,
      groupName: f.groupName ?? null,
      // The shape of the box is the form's, not this send's, so it is
      // always taught — a fold the sender accepted on screen is what the
      // next application of this map should open with.
      ...multilineOf(f),
      ...styleOf(f),
      anchor: f.anchor ?? null,
    });
  }
  return out;
}

/** The style a locked text box carries, validated, or nothing. The shape
 *  of a form's own type is the form's, so it is taught and reapplied. */
function styleOf(f: { textStyle?: unknown }): { textStyle?: EsignTextStyle } {
  const textStyle = parseTextStyle(f.textStyle);
  return textStyle ? { textStyle } : {};
}

/** The multi-line facts a field carries, or nothing at all. Spread
 *  rather than assigned so a single-line box never gains the keys set
 *  to null/undefined (`exactOptionalPropertyTypes`). */
function multilineOf(f: {
  multiline?: boolean | null;
  linePitch?: number | null;
  firstLineIndent?: number | null;
}): { multiline?: true; linePitch?: number; firstLineIndent?: number } {
  if (f.multiline !== true || typeof f.linePitch !== "number") return {};
  return {
    multiline: true,
    linePitch: f.linePitch,
    ...(typeof f.firstLineIndent === "number" && f.firstLineIndent > 0
      ? { firstLineIndent: f.firstLineIndent }
      : {}),
  };
}

// ── Making room for the signers a saved map was taught with ────────────
//
// Baker, 2026-09-14: "When I map this document and hit save it is not
// saving. New boxes added and saved. Close and re-open all the boxes
// are gone."
//
// They were saving. The mapping screen opened with a fixed two signer
// slots every time, and applyLayout above drops any field whose ordinal
// has no signer today — so a map taught with four signers came back
// holding only the first two signers' fields, and the next save wrote
// that truncated version over the good one. Each reopen lost more.
//
// The fix is this function: before a saved map is applied, the slot list
// grows to fit what the map was actually taught with. Growing is the
// only direction it goes — a slot the person renamed or added stays
// exactly as they left it, and generated slots only ever land after
// them.
export interface MappingSlotShape {
  id: string;
  label: string;
  /** The catalog seat this slot stands for, when it has been named. */
  roleKey?: string | null;
}

/** Hard ceiling on numbered mapping slots, shared by the screen that
 *  draws them and the route that fills them in, so the two cannot drift
 *  into disagreeing about how many a map may have. */
export const MAX_MAPPING_SLOTS = 8;

/**
 * The slot list a saved layout needs in order to place all of its
 * fields, starting from the slots the screen already has.
 *
 * Returns the ORIGINAL array unchanged when nothing needs adding, so a
 * caller can use identity to tell "grew" from "did not".
 *
 * Deliberately reads the fields' own ordinals as well as the layout's
 * recorded signer count: the count is what the teaching send reported,
 * and a map repaired by hand or written by an older build can carry a
 * field whose ordinal runs past it. Placing that field is the whole
 * point, so the wider of the two wins.
 */
export function slotsForSavedLayout(
  provided: readonly MappingSlotShape[],
  layoutFields: readonly { signerIndex: number; roleKey?: string | null; signerRole?: string | null }[],
  layoutSignerCount: number,
  maxSlots: number = MAX_MAPPING_SLOTS,
): MappingSlotShape[] {
  let needed = Math.max(
    provided.length,
    Number.isFinite(layoutSignerCount) ? layoutSignerCount : 0,
  );
  for (const f of layoutFields) {
    if (Number.isInteger(f.signerIndex) && f.signerIndex + 1 > needed) needed = f.signerIndex + 1;
  }
  needed = Math.min(needed, maxSlots);

  // THE SEATS THE MAP WAS TAUGHT WITH COME BACK TOO (2026-09-15).
  //
  // A map taught as "Buyer 1 / Buyer 2" reopens on a screen whose slots
  // are called "Signer 1 / Signer 2" and know nothing. Restoring each
  // seat onto the slot that held its fields is what makes re-opening a
  // map show the map — and it is deterministic, because the field's own
  // ordinal says which slot it sat at. A slot the mapper has already
  // named is never overwritten: their answer outranks the stored one.
  const seatByIndex = new Map<number, { roleKey: string; label: string | null }>();
  for (const f of layoutFields) {
    if (!f.roleKey || f.signerIndex < 0 || seatByIndex.has(f.signerIndex)) continue;
    seatByIndex.set(f.signerIndex, { roleKey: f.roleKey, label: f.signerRole ?? null });
  }
  // Rebuilt ONLY when a seat is actually restored, so the documented
  // identity contract above ("returns the ORIGINAL array unchanged when
  // nothing needs adding") still holds for every map with no seats —
  // which is every map taught before today.
  let restoredAny = false;
  const withSeats = provided.map((slot, i) => {
    if (slot.roleKey) return slot;
    const seat = seatByIndex.get(i);
    if (!seat) return slot;
    restoredAny = true;
    return { ...slot, roleKey: seat.roleKey, label: seat.label ?? slot.label };
  });

  if (needed <= provided.length) {
    return restoredAny ? withSeats : (provided as MappingSlotShape[]);
  }

  const out = [...withSeats];
  // A generated id must not land on one the screen is already using —
  // a collision would point two slots at the same fields.
  const taken = new Set(out.map((s) => s.id));
  for (let i = out.length; i < needed; i += 1) {
    let id = `slot-${i}`;
    let bump = 0;
    while (taken.has(id)) {
      bump += 1;
      id = `slot-${i}-${bump}`;
    }
    taken.add(id);
    // Same rule as slotsFromSignerHints: the seat is attached only when
    // there is one, so a slot with no seat keeps the exact shape it has
    // always had. Callers compare these objects whole.
    const seat = seatByIndex.get(i);
    out.push(
      seat
        ? { id, label: seat.label ?? `Signer ${i + 1}`, roleKey: seat.roleKey }
        : { id, label: `Signer ${i + 1}` },
    );
  }
  return out;
}

/**
 * May a send re-teach the map this document already has?
 *
 * Grant, 2026-09-14: "don't let sends flatten the map."
 *
 * Teaching is positional — a send can only speak for the ordinals it
 * actually has — so a send with fewer signers than the stored map
 * rewrites it down to its own size, deleting the higher signers' fields
 * outright. That is how a nine-field, four-signer mortgage
 * authorization became a four-field, one-signer one: somebody sent it
 * to a single borrower.
 *
 * The rule is the narrowest one that stops it. A send with at least as
 * many signers as the stored map still teaches, so the map keeps
 * improving with ordinary work. Only the send that cannot know about
 * the positions it lacks is asked to leave them alone — and leaving
 * them alone is the reversible side of the trade, since the next full
 * send teaches anyway while an overwrite is gone.
 *
 * No stored map (`null`) always teaches: there is nothing to flatten.
 */
export function sendMayReteachLayout(
  sendSignerCount: number,
  priorSignerCount: number | null,
): boolean {
  if (priorSignerCount === null || !Number.isFinite(priorSignerCount)) return true;
  return sendSignerCount >= priorSignerCount;
}

// ── WHO A FORM IS SIGNED BY (Grant, 2026-10-27) ──────────────────────────
//
// "the commission disbursement form is not a form that is for the
// sellers/buyers at all... there is literally no where for daniel or amelia
// to sign. so why is the OS suggestion to have daniel and amelia sign it?"
//
// A form's saved map already says who signs it: every signer-owned box is
// keyed to a seat. This reads that list so the send can cast only those
// seats. The answer is "unknown" whenever the map cannot say for sure, and
// unknown always means "cast the record as before", never "cast nobody".

/** The seats a saved map has SIGNER boxes for. `known: false` when the map
 *  has no signer boxes keyed to a seat it can name (a map saved before seats
 *  existed, 2026-09-15, keys boxes by position only), so the caller falls
 *  back to casting the whole record. A box the sender fills (the preparer's)
 *  names no signer and is never counted. */
export function seatsAFormSigns(
  fields: readonly { signerIndex: number; roleKey?: string | null }[],
): { known: boolean; seats: string[] } {
  const seats = new Set<string>();
  let unkeyed = 0;
  for (const f of fields) {
    if (f.signerIndex === PREPARER_LAYOUT_INDEX) continue;
    if (f.roleKey) seats.add(f.roleKey);
    else unkeyed += 1;
  }
  if (unkeyed > 0) return { known: false, seats: [] };
  return { known: true, seats: [...seats].sort() };
}

/** Every document's answer rolled into one for the whole send: known only
 *  when EVERY document is, because one unmapped document could be the one a
 *  buyer signs. */
export function seatsAPackageSigns(
  perDocument: readonly { known: boolean; seats: readonly string[] }[],
): ReadonlySet<string> | null {
  if (perDocument.length === 0) return null;
  if (perDocument.some((d) => !d.known)) return null;
  return new Set(perDocument.flatMap((d) => d.seats));
}
